import { action, computed, makeObservable, observable } from 'mobx';
import {
  ApiBooking,
  ApiBookingCancelRequest,
  ApiBookingLoginRequest,
  ApiChangeServiceDateRequest,
  ApiDeleteTokenForBookingRequest,
  ApiErrorResponseFromJSON,
  ApiPreparePaymentRequest,
  ApiPreparePaymentRequestFromJSON,
  ApiSendBookingListRequest,
  ApiTicketResponse,
  ApiTraveler,
  ApiUpdateCustomerRequest,
  ApiUpdateNotesRequest,
  ApiUpdateServiceNotesRequest
} from '@ibe/api';
import { ApiService, AuthService, clone, Headers } from '@ibe/services';
import LoginService from './LoginService';

class BookingService {
  private ERR_NO_API = 'Can not init booking: No API instance.';

  private ERR_NO_AUTHORIZATION_SERVICE = 'No authorization instance.';

  private api: ApiService | null = null;

  public _isBusy = false;

  public _booking: ApiBooking | null = null;

  private authService: AuthService | null = null;

  private loginService: LoginService = new LoginService();

  constructor() {
    makeObservable(this, {
      _booking: observable,
      _isBusy: observable,
      booking: computed,
      isBusy: computed,
      getBooking: action
    });
  }

  get booking(): ApiBooking | null {
    return this._booking ? (clone(this._booking) as ApiBooking) : null;
  }

  get isBusy(): boolean {
    return this._isBusy;
  }

  async getBooking(bookingNumber?: string, noCache?: boolean): Promise<ApiBooking> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }
    this._isBusy = true;
    try {
      if (bookingNumber) {
        const response = await this.api.getBookingRaw({
          bookingId: bookingNumber,
          noCache: noCache
        });
        const token = response.raw.headers.get(Headers.AUTHORIZATION);
        this._booking = await response.value();
        if (token) {
          await this.authService?.authenticate(token);
        }
        await this.loginService.refreshToken();
        this._isBusy = false;

        return this._booking;
      }
    } catch (e) {
      this._booking = null;
      this._isBusy = false;
      throw ApiErrorResponseFromJSON(await (e as Response).json());
    }
    return Promise.reject();
  }

  async searchBookingByToken(bookingToken: string): Promise<ApiBooking | null> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }
    if (!this.authService) {
      throw new Error(this.ERR_NO_AUTHORIZATION_SERVICE);
    }
    this._isBusy = true;
    try {
      const response = await this.api.searchBookingByTokenRaw({ bookingToken: bookingToken });
      const token = response.raw.headers.get(Headers.AUTHORIZATION);
      if (token) {
        this.authService.state.token = token;

        this._booking = await response.value();
        this._isBusy = false;
      }
      this._isBusy = false;

      return this._booking;
    } catch (e) {
      this._booking = null;
      this._isBusy = false;
      throw ApiErrorResponseFromJSON(await (e as Response).json());
    }
  }

  async doBookingLogin(bookingLoginRequest?: ApiBookingLoginRequest): Promise<ApiBooking | null> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }
    if (!this.authService) {
      throw new Error(this.ERR_NO_AUTHORIZATION_SERVICE);
    }

    this._isBusy = true;
    try {
      if (bookingLoginRequest) {
        const response = await this.api.bookingLoginRaw({
          apiBookingLoginRequest: bookingLoginRequest
        });

        const token = response.raw.headers.get(Headers.AUTHORIZATION);
        if (token) {
          this.authService.authenticate(token);

          this._booking = await response.value();
          this._isBusy = false;
        }
        return this._booking;
      }
    } catch (e) {
      this._booking = null;
      this._isBusy = false;
      throw ApiErrorResponseFromJSON(await (e as Response).json());
    }
    return Promise.reject();
  }

  async sendBookingListToCustomerRaw(participantLastName: string, email: string): Promise<void> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }
    this._isBusy = true;
    try {
      if (participantLastName && email) {
        const rq: ApiSendBookingListRequest = {
          lastName: participantLastName,
          emailAddress: email
        };
        await this.api.sendBookingListToCustomer(rq);
        this._isBusy = false;
      }
    } catch (e) {
      this._isBusy = false;
      throw ApiErrorResponseFromJSON(await (e as Response).json());
    }
    return Promise.resolve();
  }

  async sendTokenForBooking(bookingLoginRequest: ApiBookingLoginRequest): Promise<void> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }
    this._isBusy = true;
    try {
      await this.api.sendTokenForBooking(bookingLoginRequest);
      this._isBusy = false;
    } catch (e) {
      this._isBusy = false;
      throw ApiErrorResponseFromJSON(await (e as Response).json());
    }
    return Promise.resolve();
  }

  async deleteTokenForBooking(
    deleteTokenForBookingRequest: ApiDeleteTokenForBookingRequest
  ): Promise<void> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }
    this._isBusy = true;
    try {
      await this.api.deleteTokenForBooking(deleteTokenForBookingRequest);
      this._isBusy = false;
    } catch (e) {
      this._isBusy = false;
      throw ApiErrorResponseFromJSON(await (e as Response).json());
    }
    return Promise.resolve();
  }

  async getTicketInWallet(bookingNumber: string): Promise<Array<ApiTicketResponse>> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }
    this._isBusy = true;
    try {
      if (bookingNumber) {
        const response = await this.api.getTicketInWallet(bookingNumber);
        await this.loginService.refreshToken();
        this._isBusy = false;

        return response;
      }
    } catch (e) {
      this._isBusy = false;
      throw ApiErrorResponseFromJSON(await (e as Response).json());
    }
    return Promise.reject();
  }

  async cancelBooking(rq: ApiBookingCancelRequest, skipObservable?: boolean): Promise<ApiBooking> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }

    if (!skipObservable) {
      this._isBusy = true;
    }

    try {
      if (rq) {
        const response = await this.api.bookingCancel(rq);
        if (!skipObservable) {
          await this.loginService.refreshToken();
          this._booking = await response;
          this._isBusy = false;
        }
        return await response;
      }
    } catch (e) {
      if (!skipObservable) {
        this._booking = null;
        this._isBusy = false;
      }
      throw ApiErrorResponseFromJSON(await (e as Response).json());
    }
    return Promise.reject();
  }

  async updateNotes(rq: ApiUpdateNotesRequest): Promise<ApiBooking> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }
    this._isBusy = true;
    try {
      const bookingNumber = this._booking?.bookingNumber;
      if (rq && bookingNumber) {
        const response = await this.api.updateNotes(bookingNumber, rq);
        await this.loginService.refreshToken();
        this._booking = await response;
        this._isBusy = false;
        return await response;
      }
    } catch (e) {
      this._booking = null;
      this._isBusy = false;
      throw ApiErrorResponseFromJSON(await (e as Response).json());
    }
    return Promise.reject();
  }

  async updateTraveler(
    traveler: ApiTraveler,
    bookingNo?: string,
    attempt?: boolean
  ): Promise<ApiBooking> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }
    if (!attempt) {
      this._isBusy = true;
    }

    try {
      const bookingNumber = bookingNo || this._booking?.bookingNumber;
      if (traveler && bookingNumber) {
        const response = await this.api.updateTraveler(bookingNumber, attempt || false, traveler);
        if (!attempt) {
          await this.loginService.refreshToken();
          this._booking = await response;
          this._isBusy = false;
        }
        return await response;
      }
    } catch (e) {
      if (!attempt) {
        this._isBusy = false;
      }
      throw ApiErrorResponseFromJSON(await (e as Response).json());
    }
    if (!attempt) {
      this._isBusy = false;
    }
    return Promise.reject();
  }

  async updateServiceDate(
    rq: ApiChangeServiceDateRequest,
    skipObservable?: boolean
  ): Promise<ApiBooking> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }
    if (!skipObservable) {
      this._isBusy = true;
    }
    try {
      if (rq) {
        const response = await this.api.changeDate(rq);
        if (!skipObservable) {
          await this.loginService.refreshToken();
          this._booking = await response;
          this._isBusy = false;
        }

        return await response;
      }
    } catch (e) {
      if (!skipObservable) {
        this._isBusy = false;
      }
      throw ApiErrorResponseFromJSON(await (e as Response).json());
    }
    if (!skipObservable) {
      this._isBusy = false;
    }
    return Promise.reject();
  }

  async pay(
    bookingNumber: string,
    preparePaymentRequest?: ApiPreparePaymentRequest,
    skipObservable?: boolean
  ): Promise<string> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }

    if (!skipObservable) {
      this._isBusy = true;
    }

    try {
      if (bookingNumber) {
        const response = await this.api.preparePayment(
          bookingNumber,
          preparePaymentRequest || ApiPreparePaymentRequestFromJSON({ isDeposit: false })
        );

        if (!skipObservable) {
          await this.loginService.refreshToken();
          this._isBusy = false;
        }

        return response;
      }
    } catch (e) {
      if (!skipObservable) {
        this._isBusy = false;
      }
      throw ApiErrorResponseFromJSON(e);
    }
    return Promise.reject();
  }

  async updateCustomer(
    bookingNumber: string,
    rq: ApiUpdateCustomerRequest,
    skipObservable?: boolean
  ): Promise<ApiBooking> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }
    if (!skipObservable) {
      this._isBusy = true;
    }
    try {
      if (bookingNumber && rq) {
        const response = await this.api.updateCustomer(bookingNumber, rq);
        if (!skipObservable) {
          await this.loginService.refreshToken();
          this._booking = await response;
          this._isBusy = false;
        }

        return await response;
      }
    } catch (e) {
      if (!skipObservable) {
        this._isBusy = false;
      }
      throw ApiErrorResponseFromJSON(await (e as Response).json());
    }
    if (!skipObservable) {
      this._isBusy = false;
    }
    return Promise.reject();
  }

  async refreshBooking(): Promise<void> {
    if (this._booking?.bookingNumber) {
      await this.getBooking(this._booking?.bookingNumber);
    }
  }

  async updateServiceNotes(
    rq: ApiUpdateServiceNotesRequest,
    attempt: boolean
  ): Promise<ApiBooking> {
    if (!this.api) {
      throw new Error(this.ERR_NO_API);
    }
    this._isBusy = true;
    try {
      if (rq) {
        const response = await this.api.updateServiceNotes(attempt, rq);
        await this.loginService.refreshToken();
        this._booking = await response;
        this._isBusy = false;
        return await response;
      }
    } catch (e) {
      this._booking = null;
      this._isBusy = false;
      throw ApiErrorResponseFromJSON(await (e as Response).json());
    }
    return Promise.reject();
  }

  setApi(api: ApiService): void {
    this.api = api;

    this.loginService.setApi(api);
  }

  setAuth(auth: AuthService): void {
    this.authService = auth;

    this.loginService.setAuth(auth);
  }
}

export default BookingService;
